import { styled } from "@mui/material/styles";
import TimelineOverlay from "_shared/_views/timelines/timeline-overlay";
import useScrollbarPress from "_shared/_views/timelines/use-scrollbar-press";
import { type ReactNode, type UIEvent, useEffect, useRef } from "react";
import type { CommentTimeline } from "work/proposals/comments/_view/use-comment-timeline";

// The scrubber is positioned against this, not against the list: inside the list it would scroll away
// with the comments.
const Frame = styled("div")(() => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minHeight: 0,
  position: "relative",
}));
const Scroller = styled("div")(() => ({
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
}));

/** What a list says when the date filter leaves none of its comments. */
export const filteredEmptyText = "No comments from these dates";

type CommentTimelineFrameProps = {
  timeline: CommentTimeline;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
};

/**
 * The Correspondence pane's scrolling comment list, with the timeline scrubber over its right edge. The
 * search bar's date toggle opens the scrubber, as it does over the work agreement's chat.
 *
 * It owns the scroll container because both ways into the scrubber hang off it: grabbing its scrollbar
 * brings the scrubber up, and scrolling it holds a scrubber that came up that way.
 */
export default function CommentTimelineFrame(props: Readonly<CommentTimelineFrameProps>) {
  const { timeline, children, className, onScroll } = props;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { openFromScrollbar, fade } = timeline;

  useScrollbarPress(scrollerRef, openFromScrollbar);

  // Bottom-up (#5741), so the foot of the list is the newest comment a pick leaves in it, and a pick
  // lands the reader there the way opening the pane does. `scrollTo` is optional-called because jsdom
  // has none.
  const filterStart = timeline.filteredTo?.start.valueOf();
  const filterEnd = timeline.filteredTo?.end.valueOf();
  useEffect(() => {
    const scroller = scrollerRef.current;
    scroller?.scrollTo?.({ top: scroller.scrollHeight });
  }, [filterStart, filterEnd]);

  return (
    <Frame>
      <Scroller
        ref={scrollerRef}
        className={className}
        data-testid={props["data-testid"]}
        onScroll={(event) => {
          fade.noteActivity();
          onScroll?.(event);
        }}
      >
        {children}
      </Scroller>
      <TimelineOverlay
        key={timeline.generation}
        show={timeline.show}
        fading={fade.fading}
        onHover={fade.hover}
        onEngaged={fade.engage}
        firstDate={timeline.firstDate}
        lastDate={timeline.lastDate}
        onDateSelected={timeline.selectDates}
        onReset={timeline.reset}
      />
    </Frame>
  );
}
